import {
    Button,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils';
import type { AdminProcess } from '../processes/adminProcessDetailsTypes';
import { StatusLabelStatus } from '../../../../common/enums/statusLabelStatus';
import { ToastType } from '../../../../common/enums/toastType';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { showToast } from '../../../utils/showToast';
import { FullName, ItemBold, Loader } from '../../elements/common';
import { getStatusLabel } from '../../elements/getStatusLabel';
import { NullableFormattedDatetime } from '../../elements/NullableFormattedDatetime';
import { StatusLabelManager } from '../../elements/StatusLabelManager';

export function FillDummyEctosenseReports() {
    const [processes, setProcesses] = useState<AdminProcess[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    assertHasAuthorization(ManagerAuthorization.adminProcessRead);

    useEffect(() => {
        loadProcesses();
    }, []);

    return (
        <PageTemplate>
            <PageHeader title={'Fill Dummy Ectosense Reports'} />
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('service.service')}</TableHeader>
                            <TableHeader>{translateKey('common.pid')}</TableHeader>
                            <TableHeader>{translateKey('patient.patient')}</TableHeader>
                            <TableHeader>{translateKey('patient.identifierSocialSecurity')}</TableHeader>
                            <TableHeader>{translateKey('patient.careProvider')}</TableHeader>
                            <TableHeader>{translateKey('common.created')}</TableHeader>
                            <TableHeader>{translateKey('common.status')}</TableHeader>
                            <TableHeader></TableHeader>
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {processes.map((osasProcess, i) => (
                            <TableRow key={i}>
                                <TableCell>{osasProcess.service.code}</TableCell>
                                <TableCell>
                                    <ItemBold>{osasProcess.processId}</ItemBold>
                                </TableCell>
                                <TableCell>
                                    <FullName {...osasProcess.patient} />
                                </TableCell>
                                <TableCell>{osasProcess.patient.identifierSocialSecurity}</TableCell>
                                <TableCell>
                                    <ItemBold>{getName(osasProcess)}</ItemBold>
                                </TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={osasProcess.created_at} />
                                </TableCell>
                                <TableCell>
                                    <StatusLabelManager
                                        badge
                                        request={osasProcess}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        className={`automation-sendOneReport-${i}`}
                                        color="secondary"
                                        isDisabled={isButtonDisabled(osasProcess)}
                                        onClick={() => sendTestEctosenseReports(osasProcess.id, 1)}
                                        variant={'outline'}
                                    >
                                        Send 1 Ectosense Report
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        className={`automation-sendThreeReports-${i}`}
                                        color="secondary"
                                        isDisabled={isButtonDisabled(osasProcess)}
                                        onClick={() => sendTestEctosenseReports(osasProcess.id, 3)}
                                        variant={'outline'}
                                    >
                                        Send 3 Ectosense Reports
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {isLoading && (
                <Loader
                    fixedCentered
                    large
                />
            )}
        </PageTemplate>
    );

    function getName(process: AdminProcess) {
        if (process.recipientPractitioner) {
            return <FullName {...process.recipientPractitioner} />;
        } else if (process.recipientOrganization) {
            return process.recipientOrganization.name;
        }

        return null;
    }

    function isButtonDisabled(process: AdminProcess) {
        const statusLabel = getStatusLabel(process);

        return statusLabel !== StatusLabelStatus.requested && statusLabel !== StatusLabelStatus.withPatient;
    }

    async function loadProcesses() {
        const osasAdminProcesses = [];
        const osasoServiceResult = await fetchJson('/api/services?code=OSASO', 'GET');

        if (osasoServiceResult.json.total > 0) {
            const result = await fetchJson('/api/adminProcesses', 'POST', {
                serviceId: osasoServiceResult.json.items[0].id,
            });

            if (result.json.items) {
                osasAdminProcesses.push(...result.json.items);
            }
        }

        const osasvoServiceResult = await fetchJson('/api/services?code=OSASVO', 'GET');

        if (osasvoServiceResult.json.total > 0) {
            const result = await fetchJson('/api/adminProcesses', 'POST', {
                serviceId: osasvoServiceResult.json.items[0].id,
            });

            if (result.json.items) {
                osasAdminProcesses.push(...result.json.items);
            }
        }

        setProcesses(
            osasAdminProcesses.sort((a, b) => {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            })
        );
    }

    async function sendTestEctosenseReports(requestId: string, numberOfReports: number) {
        setIsLoading(true);

        const url = '/api/fillDummyEctosenseReports';
        const result = await fetchJson(url, 'POST', { requestId, numberOfReports });

        if (result.json.ectosenseTestUpdateJobResult) {
            showToast(ToastType.success, result.json.ectosenseTestUpdateJobResult);
        } else if (result.json.errorMessage) {
            showToast(ToastType.error, result.json.errorMessage);
        }

        setIsLoading(false);
    }
}
